import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Container from '../components/layout/Container';
import SectionTitle from '../components/ui/SectionTitle';
import Button from '../components/ui/Button';

interface FAQItemProps {
  question: string;
  answer: string;
  index: number;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      className="border-b border-neutral-200 py-6"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <button
        className="flex justify-between items-center w-full text-left"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <h3 className="text-xl font-semibold pr-8">{question}</h3>
        <span className="text-primary-500 flex-shrink-0">
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="pt-4 text-neutral-600">{answer}</div>
      </motion.div>
    </motion.div>
  );
};

const FAQPage: React.FC = () => {
  const faqCategories = [
    {
      name: 'General',
      faqs: [
        {
          question: 'What is IDX.rainautomation.com?',
          answer: 'IDX.rainautomation.com is a premium, multi-tiered IDX platform built for real estate agents and brokerages. We offer high-end search, lead capture, SEO-optimized pages, and AI-driven features that integrate with the FMLS Bridge API.',
        },
        {
          question: 'How does IDX.rainautomation.com differ from other IDX providers?',
          answer: 'We focus on premium design, performance, and AI-driven features. Our platform is built with a mobile-first approach, emphasis on SEO, and offers customizable features across different subscription tiers to fit your specific needs.',
        },
        {
          question: 'Which MLS systems do you support?',
          answer: 'Currently, we support the FMLS (First Multiple Listing Service) area, with plans to expand to additional MLS systems in the future. Contact us to inquire about specific MLS integration plans.',
        },
        {
          question: 'Do I need technical skills to use your platform?',
          answer: 'No technical skills are required. Our platform is designed to be user-friendly with an intuitive dashboard. For more advanced customizations, our support team is available to assist you.',
        },
      ],
    },
    {
      name: 'Features & Functionality',
      faqs: [
        {
          question: 'What search features are available?',
          answer: 'All plans include basic property search with filters for price, beds, baths, and more. Advanced features like polygon map search, radius search, and AI-powered predictive search are available in higher tiers.',
        },
        {
          question: 'Can I customize the design of my IDX pages?',
          answer: 'Yes, all plans offer some level of customization. The Elite plan provides full white-labeling and extensive design customization to match your brand perfectly.',
        },
        {
          question: 'How do the AI features work?',
          answer: 'Our AI tools help with property descriptions, social media captions, and personalized search recommendations. They analyze property data and user behavior to generate relevant content and improve user experience.',
        },
        {
          question: 'Can I embed IDX search on my existing website?',
          answer: 'Yes, the Engage and Elite plans include embeddable widgets that can be added to your existing website on platforms like WordPress, Squarespace, and Wix.',
        },
      ],
    },
    {
      name: 'Billing & Pricing',
      faqs: [
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover) and can arrange for ACH payments for annual subscriptions.',
        },
        {
          question: 'Is there a setup fee?',
          answer: 'Every IDX site is quoted for the agent or team after a short call, and the quote includes onboarding and MLS configuration.',
        },
        {
          question: 'Can I upgrade or downgrade my plan?',
          answer: 'Yes, you can upgrade at any time and the price difference will be prorated. Downgrades take effect at the end of your current billing cycle.',
        },
        {
          question: 'Do you offer refunds?',
          answer: 'We offer a 14-day money-back guarantee for new subscribers. If you\'re not satisfied with our service, contact us within 14 days of signing up for a full refund.',
        },
      ],
    },
    {
      name: 'Support & Training',
      faqs: [
        {
          question: 'What kind of support do you offer?',
          answer: 'All plans include premium support via email, chat, and phone during business hours. Elite subscribers also receive priority support with a dedicated account manager.',
        },
        {
          question: 'Do you provide training?',
          answer: 'Yes, we offer onboarding training sessions for all new clients, plus access to our knowledge base and video tutorials. Elite subscribers receive additional advanced training sessions.',
        },
        {
          question: 'What is your uptime guarantee?',
          answer: 'We guarantee 99.9% uptime for all our services, with real-time monitoring and alerts to ensure your IDX solution is always available to your clients.',
        },
        {
          question: 'How quickly do you respond to support inquiries?',
          answer: 'We aim to respond to all support inquiries within 2 hours during business hours (9am-6pm ET, Monday-Friday) and within 24 hours on weekends.',
        },
      ],
    },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqCategories.flatMap(category =>
      category.faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    )
  };

  return (
    <>
      <Helmet>
        <title>Frequently Asked Questions | IDX.rainautomation.com</title>
        <meta
          name="description"
          content="Find answers to common questions about IDX.rainautomation.com, including features, pricing, MLS support, and AI tools for real estate agents."
        />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is rAIn IDX?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "rAIn IDX is a modern MLS integration platform for real estate agents, offering embedded listings, lead capture, and SEO tools."
                }
              },
              {
                "@type": "Question",
                "name": "How much does it cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Every IDX site is quoted for the agent or team after a short call. Brokerage and multi-agent deployments are quoted separately."
                }
              }
            ]
          }
          `}
          </script>
      </Helmet>

      <section className="pt-32 pb-16">
        <Container>
          <h1 className="sr-only">Frequently Asked Questions</h1>
          <SectionTitle
            title="Frequently Asked Questions"
            subtitle="Find answers to common questions about our IDX platform"
            centered
          />

          <div className="max-w-4xl mx-auto">
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-12">
                <h2 className="text-2xl font-bold font-serif mb-6">{category.name}</h2>
                <div className="bg-white rounded-lg shadow-soft overflow-hidden">
                  {category.faqs.map((faq, faqIndex) => (
                    <FAQItem
                      key={faqIndex}
                      question={faq.question}
                      answer={faq.answer}
                      index={faqIndex}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-primary-50 rounded-lg p-8 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold font-serif mb-4 text-center">
              Didn't find what you're looking for?
            </h2>
            <p className="text-neutral-600 text-center mb-6">
              Our support team is ready to help with any questions you might have.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button variant="primary" to="/contact">
                Contact Support
              </Button>
              <Button variant="outline" to="/features">
                Explore Features
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
};

export default FAQPage;